import type { AssistantTurn, PendingAction } from '@secretario/shared';
import { useState } from 'react';
import { decideAction } from '../../lib/api/assistant.js';
import { describeError } from '../../lib/api/http.js';
import { markDraftHandled } from '../../lib/assistantDraft.js';
import { hapticSuccess, hapticTap } from '../../lib/native.js';
import './assistant-modal.css';

/**
 * A ação esperando o sim (F0): o resumo do que vai ser feito e os dois botões. O toque decide
 * no servidor, sem o modelo; o que roda é o pedido guardado. Risco alto só aceita este toque.
 */
export function PendingActionCard({
  pending,
  onDecided,
  onChange,
  tone = 'light',
}: {
  pending: PendingAction;
  /** A resposta do Secretário depois do toque (já guardada na conversa). */
  onDecided: (turn: AssistantTurn | null, state: string) => void;
  /** "Mudar": a pessoa diz o que quer diferente (o campo de pedido ganha o foco). */
  onChange?: () => void;
  /** No balão escuro da clínica, os botões seguem o fundo. */
  tone?: 'light' | 'dark';
}) {
  const [busy, setBusy] = useState<'sim' | 'nao' | null>(null);
  const [error, setError] = useState('');

  async function decide(decision: 'sim' | 'nao') {
    if (busy) return;
    setBusy(decision);
    setError('');
    hapticTap();
    try {
      const result = await decideAction(pending.id, decision);
      markDraftHandled(pending.id);
      if (decision === 'sim' && result.state === 'feito') hapticSuccess();
      onDecided(result.turn, result.state);
      if (decision === 'nao') onChange?.();
    } catch (cause) {
      setError(describeError(cause, 'Não consegui agora. Tenta de novo?'));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section
      className={`am-draft am-confirm am-pending${tone === 'dark' ? ' dark' : ''}`}
      aria-label="Confirmar a ação"
    >
      <p className="am-pending-q">{pending.summary}</p>
      {pending.risk === 'alto' && (
        <small className="am-pending-note">Essa só vale com o seu toque aqui.</small>
      )}
      {error && <small className="am-pending-note bad">{error}</small>}
      <div className="am-draft-acts">
        <button
          type="button"
          className={pending.risk === 'alto' ? 'am-primary danger' : 'am-primary'}
          disabled={busy !== null}
          onClick={() => void decide('sim')}
        >
          {busy === 'sim' ? 'Fazendo…' : pending.yes}
        </button>
        <button
          type="button"
          className="am-ghost"
          disabled={busy !== null}
          onClick={() => void decide('nao')}
        >
          Mudar
        </button>
      </div>
    </section>
  );
}
